import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Account } from 'entities/account/model/types';
import { AppDispatch, RootState } from 'shared/config/store/store';
import { getEMICurrentAccountObject } from 'shared/config/store/reducers/accountsSlice';
import { CStatus, CType } from 'entities/card/model/types';
import { getAccountCards, setCards, setCurrentCard } from 'shared/config/store/reducers/cardsSlice';
import {
  useReplacePlasticMutation,
  useReplaceVirtualMutation
} from 'features/card/replace-card/api';
import { Button, ButtonProps, Modal } from 'antd';
import { getCurrentEmi } from 'shared/config/store/selectors/app';
import { useIntl } from 'react-intl';
import { setActivity } from 'shared/config/store/reducers/appSlice';
import { useMessage } from 'shared/libs/hooks/useMessage/useMessage';
import { useNavigate } from 'react-router';
import { ApiError } from 'shared/model/types';
import { DELIVERY_TYPE } from '../const';
import { ROUTES } from 'shared/const/routes';

type ReplaceCardProps = {
  label?: string;
} & Partial<ButtonProps>;

const ReplaceCard: React.FC<ReplaceCardProps> = ({ label, ...buttonProps }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { formatMessage: t } = useIntl();
  const { showError, showSuccess } = useMessage();
  const [isModalVisible, setIsModalVisible] = useState(false);

  const currentEmi = useSelector(getCurrentEmi);
  const currentAccount: Account = useSelector((state: RootState) =>
    getEMICurrentAccountObject(state, currentEmi)
  ) as Account;
  const card = useSelector(getAccountCards(currentEmi, currentAccount.id))?.[0];

  const [replacePlastic] = useReplacePlasticMutation();
  const [replaceVirtual] = useReplaceVirtualMutation();

  const onClick = () => {
    if (card.status !== CStatus.Blocked) return;

    setIsModalVisible(true);
  };

  const handleModalOk = () => {
    setIsModalVisible(false);
    onReplace();
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  const onReplacePlastic = async () => {
    dispatch(setActivity(true));
    await replacePlastic({
      accountId: currentAccount.id,
      cardId: card.id,
      deliveryType: DELIVERY_TYPE
    })
      .unwrap()
      .then(({ response }) => {
        showSuccess(t({ id: 'card.replace.messages.application' }));
        dispatch(
          setCards({
            accountId: currentAccount.id,
            cards: [response],
            emi: currentEmi
          })
        );
        dispatch(setCurrentCard({ emi: currentEmi, cardId: response.id }));
        navigate(ROUTES.CARD.path);
      })
      .catch((err: ApiError) => {
        const error = err.data?.errors?.[0];
        showError(error?.defaultMessage ?? t({ id: 'card.replace.messages.failed' }));
      })
      .finally(() => dispatch(setActivity(false)));
  };

  const onReplaceVirtual = async () => {
    dispatch(setActivity(true));
    await replaceVirtual({
      accountId: currentAccount.id,
      cardId: card.id
    })
      .unwrap()
      .then(({ response }) => {
        // TODO: add success message
        showSuccess('Success');
        dispatch(
          setCards({
            accountId: currentAccount.id,
            cards: [response],
            emi: currentEmi
          })
        );
        dispatch(setCurrentCard({ emi: currentEmi, cardId: response.id }));
        navigate(ROUTES.CARD.path);
      })
      .catch((err: ApiError) => {
        // TODO: add error message
        showError(err.data.errors?.[0].defaultMessage ?? 'Failed');
      })
      .finally(() => dispatch(setActivity(false)));
  };

  const onReplace = async () => {
          if (card.type === CType.Physical) {
        if (currentAccount.available >= 7) await onReplacePlastic();
        else showError(t({ id: 'card.blocked.replace.alert.desc.plastic' }, { amount: 7 }));
      } else {
        if (currentAccount.available >= 10) await onReplaceVirtual();
        else showError(t({ id: 'card.blocked.replace.alert.desc.virtual' }, { amount: 10 }));
      }
  };

  return (
    <>
      <Button {...buttonProps} onClick={onClick} type='primary' style={{backgroundColor: 'black', color: 'white'}}>
        {t({ id: 'card.blocked.replace' })}
      </Button>

      <Modal
        title={t({ id: 'card.blocked.replace.alert.title' })}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText={t({ id: 'button.simple.ok' })}
        cancelText={t({ id: 'button.simple.cancel' })}
      >
        <p>
          {t({ id: 'card.blocked.replace.alert.desc' })}
        </p>
        <p style={{ marginTop: 16, fontWeight: 'bold' }}>
          {card?.type === CType.Physical 
            ? t({ id: 'card.renew.fee.plastic' })
            : t({ id: 'card.renew.fee.virtual' })
          }
        </p>
      </Modal>
    </>
  );
};

export default ReplaceCard;
